import { lazy, type ComponentType } from 'react'
import { createBrowserRouter } from 'react-router'

import { LoginScreen } from '@/features/auth'

import { LandingShell } from '@/shell/LandingShell'
import { RootGate } from '@/shell/RootGate'

import { HomeRoute } from './HomeRoute'
import { AdminOnly } from './AdminOnly'
import { NotFoundPage } from './NotFoundPage'
import { Private } from './Private'
import { LegacyImportRedirect } from './LegacyImportRedirect'

/**
 * A screen fetched when its route is first visited, not with the entry chunk.
 *
 * Everything behind sign-in is loaded this way, so a visitor reading the
 * landing page does not download the builder, the tracker, the pack editor and
 * the admin tables to do it. Each import names the screen's own file rather
 * than its feature's barrel, and that is the point rather than a shortcut: a
 * barrel re-exports every screen of its feature, and a module that is
 * imported both ways stays in the entry chunk. The shells hold the
 * `Suspense` boundary, around their `Outlet`.
 */
// `any` is the bound React itself puts on a lazy component: each screen keeps
// its own props, and a bound of `object` would refuse the ones that take any.
function screen<K extends string, C extends ComponentType<any>>(load: () => Promise<Record<NoInfer<K>, C>>, name: K) {
  return lazy<C>(() => load().then((module) => ({ default: module[name] })))
}

const InvitationLink = screen(() => import('./InvitationRoute'), 'InvitationLink')
const InvitationRoute = screen(() => import('./InvitationRoute'), 'InvitationRoute')
const PacksScreen = screen(() => import('@/features/packs/PacksScreen'), 'PacksScreen')
const PackEditorScreen = screen(() => import('@/features/packs/PackEditorScreen'), 'PackEditorScreen')
const AvatarGalleryScreen = screen(() => import('@/features/avatars/AvatarGalleryScreen'), 'AvatarGalleryScreen')
const AccountScreen = screen(() => import('@/features/account/AccountScreen'), 'AccountScreen')
const BuildScreen = screen(() => import('@/features/character/BuildScreen'), 'BuildScreen')
const CharacterLogScreen = screen(() => import('@/features/character/CharacterLogScreen'), 'CharacterLogScreen')
const CharacterSheetScreen = screen(() => import('@/features/character/CharacterSheetScreen'), 'CharacterSheetScreen')
const CustomItemScreen = screen(() => import('@/features/character/CustomItemScreen'), 'CustomItemScreen')
const ItemScreen = screen(() => import('@/features/character/ItemScreen'), 'ItemScreen')
const AgentImportScreen = screen(() => import('@/features/characters/AgentImportScreen'), 'AgentImportScreen')
const DiceScreen = screen(() => import('@/features/dice/DiceScreen'), 'DiceScreen')
const AdminScreen = screen(() => import('@/features/admin/AdminScreen'), 'AdminScreen')
const GameScreen = screen(() => import('@/features/games/GameScreen'), 'GameScreen')
const GamesScreen = screen(() => import('@/features/games/GamesScreen'), 'GamesScreen')
const SharedSheetScreen = screen(() => import('@/features/games/SharedSheetScreen'), 'SharedSheetScreen')
const GroupListScreen = screen(() => import('@/features/groups/GroupListScreen'), 'GroupListScreen')
const GroupScreen = screen(() => import('@/features/groups/GroupScreen'), 'GroupScreen')
const LegalScreen = screen(() => import('@/features/legal/LegalScreen'), 'LegalScreen')
const SpellScreen = screen(() => import('@/features/spells/SpellScreen'), 'SpellScreen')
const SpellsScreen = screen(() => import('@/features/spells/SpellsScreen'), 'SpellsScreen')

/**
 * The complete route table -- one tree for both viewports and for both sides
 * of the sign-in boundary, the way internal/api/http/router.go is one file for
 * the whole API. Which chrome wraps it is RootGate's business; routes never
 * know the viewport, and never redirect on account of who is looking.
 *
 * Screens live in features/, not here: routes/ is the table, and a screen that
 * lives in it is drift. The exception is a page that belongs to the table
 * rather than to an aggregate -- NotFoundPage. `/characters/:id/build` is one
 * screen for creation and for level-up, because the API poses them as the same
 * question.
 *
 * nginx rewrites unknown paths to index.html, so a deep link lands here rather
 * than on the API's 404 envelope.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootGate />,
    children: [
      { index: true, element: <HomeRoute /> },
      { path: 'avatar-gallery', element: <AvatarGalleryScreen /> },
      { path: 'homebrew', element: <Private><PacksScreen /></Private> },
      { path: 'homebrew/:id', element: <Private><PackEditorScreen /></Private> },

      // A character is somebody's, so these render the landing page to a
      // signed-out visitor rather than redirecting: the URL survives being
      // shared, and signing in fills it in.
      //
      // The same screen as `/characters/:id/build`, and not by coincidence:
      // creating is answering the first question, and a separate create page
      // was a second shape for one thing. Without an `:id` it holds the name
      // alone; answering it creates the character and replaces this URL with
      // the build one.
      {
        path: 'characters/new',
        element: (
          <Private>
            <BuildScreen />
          </Private>
        ),
      },
      // Ahead of characters/:id so the literal wins over the parameter.
      {
        path: 'ai-wizard',
        element: (
          <Private>
            <AgentImportScreen />
          </Private>
        ),
      },
      {
        path: 'ai-wizard/:sessionId',
        element: <Private><AgentImportScreen /></Private>,
      },
      { path: 'characters/import', element: <LegacyImportRedirect /> },
      { path: 'characters/import/:sessionId/:importView?', element: <LegacyImportRedirect /> },
      // Not `Private`, and ahead of `characters/:id`, for the reasons
      // `groups/join` is both.
      { path: 'characters/receive', element: <InvitationLink kind="character" /> },
      {
        path: 'characters/:id',
        element: (
          <Private>
            <CharacterSheetScreen />
          </Private>
        ),
      },
      {
        path: 'characters/:id/build',
        element: (
          <Private>
            <BuildScreen />
          </Private>
        ),
      },

      // The log rather than the sheet: same character, the record instead of
      // what the record means. Not a SECTIONS entry -- it hangs off a
      // character, not off the app.
      {
        path: 'characters/:id/items/:slug',
        element: <Private><ItemScreen /></Private>,
      },
      // Writing an item the catalogue does not hold, or with an entry's id
      // changing one. A page under the sheet, so its crumb is the way back.
      {
        path: 'characters/:id/custom-item/:option?',
        element: <Private><CustomItemScreen /></Private>,
      },
      {
        path: 'characters/:id/log',
        element: (
          <Private>
            <CharacterLogScreen />
          </Private>
        ),
      },

      // Groups. A group is several people's, so like a character these
      // branch to the landing page for a signed-out visitor rather than
      // redirecting -- which is what makes an invitation link survive being
      // opened by somebody who has not signed in yet.
      {
        path: 'groups',
        element: (
          <Private>
            <GroupListScreen />
          </Private>
        ),
      },
      // Ahead of groups/:id so the literal wins over the parameter. The
      // invitation token rides in the URL fragment, which the browser never
      // sends to any server -- see features/groups/inviteToken.ts.
      //
      // Not wrapped in Private, and that is the point: this is the one deep
      // link that routinely arrives at somebody with no account at all, so
      // the token has to be saved before the branch rather than inside the
      // screen that a signed-out visitor never reaches. InvitationRoute does both.
      { path: 'groups/join', element: <InvitationLink kind="group" /> },
      // The same page from the menu, with a field to paste either kind of link into.
      { path: 'invitations', element: <InvitationRoute /> },
      {
        path: 'groups/:id',
        element: (
          <Private>
            <GroupScreen />
          </Private>
        ),
      },

      // A shared character's sheet stays under its group, because sharing is a
      // group's doing and the group is what grants the read.
      // A character its owner opened, read by its link: no group in the path,
      // because no group grants this read.
      {
        path: 'shared/:character',
        element: <Private><SharedSheetScreen /></Private>,
      },
      {
        path: 'shared/:character/items/:slug',
        element: <Private><ItemScreen /></Private>,
      },
      {
        path: 'groups/:id/characters/:character/items/:slug',
        element: <Private><ItemScreen /></Private>,
      },
      {
        // An item read from a game: the trail leads back to the game, not to a sheet.
        path: 'games/:game/characters/:character/items/:slug',
        element: <Private><ItemScreen /></Private>,
      },
      {
        path: 'groups/:id/characters/:character',
        element: (
          <Private>
            <SharedSheetScreen />
          </Private>
        ),
      },

      // Games are their own section, so they sit at the top level rather than
      // under the group they are played at -- which is also what keeps
      // the navbar lighting Games instead of Groups when one is open.
      // Not behind Private: AdminOnly answers a signed-out visitor with the
      // same not-found page as everybody else who is not a superadmin.
      {
        path: 'admin',
        element: (
          <AdminOnly>
            <AdminScreen />
          </AdminOnly>
        ),
      },
      {
        path: 'games',
        element: (
          <Private>
            <GamesScreen />
          </Private>
        ),
      },
      // The same screen as `characters/:id/custom-item`, reached from a game:
      // under /games so the trail reads Games / the game / the character.
      {
        path: 'games/:id/characters/:character/custom-item',
        element: <Private><CustomItemScreen /></Private>,
      },
      {
        path: 'games/:id',
        element: (
          <Private>
            <GameScreen />
          </Private>
        ),
      },

      // The compendium's browsable half. Private like every section -- a
      // guest session passes -- though the data is nobody's; if a public
      // browser is ever wanted, the two catalog routes in the Go router are
      // the thing to move, not this.
      {
        path: 'spells',
        element: (
          <Private>
            <SpellsScreen />
          </Private>
        ),
      },
      {
        path: 'spells/:slug',
        element: (
          <Private>
            <SpellScreen />
          </Private>
        ),
      },

      // The way in. Public by necessity, and the one route that redirects on
      // account of who is looking -- a signed-in visitor is sent to the app,
      // because a login page inside the signed-in shell is nonsense.
      { path: 'login', element: <LoginScreen /> },

      // How this account is reached: its passkeys and its connected
      // providers. Private, because it is an inventory of somebody's ways in.
      {
        path: 'account',
        element: (
          <Private>
            <AccountScreen />
          </Private>
        ),
      },

      /*
       * A die, as a page.
       *
       * Deliberately absent from `ui/sections.ts`: it is not a section of the
       * app, it owns no other paths and it lights nothing in the desktop
       * navbar, which never offers it -- the die is a phone's, and
       * `shell/MobileShell.tsx` is the only chrome that links here. Private
       * only because the menu that reaches it is; the signed-out visitor has
       * their own die on the landing page.
       */
      {
        path: 'roll',
        element: (
          <Private>
            <DiceScreen />
          </Private>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },

  // /legal sits outside RootGate: a licence notice you have to sign in to read
  // is not a notice. The SRD 5.1 data is CC-BY-4.0 and that licence expects its
  // attribution in the product, so the landing footer links here and this
  // renders for everybody. Absent from ui/sections.ts -- it is a document, not a
  // section of the app.
  {
    path: '/legal',
    element: <LandingShell />,
    children: [{ index: true, element: <LegalScreen /> }],
  },
])
